import {
  Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CustomFlexLayoutModule } from 'src/app/shared/flex-layout/custom-flex-layout.module';
import { CollaborationService } from 'src/app/services/collaboration.service';
import { MentionHighlightPipe } from 'src/app/shared/pipes/mention-highlight.pipe';

/**
 * CollaborationNotesComponent
 *
 * Renders a threaded note/comment feed for any CRM resource.
 *
 * Usage:
 *   <calsoft-collaboration-notes
 *     resourceType="LEAD"
 *     [resourceId]="lead.entityId"
 *     [currentUser]="currentUser">
 *   </calsoft-collaboration-notes>
 */
@Component({
  selector: 'calsoft-collaboration-notes',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatTooltipModule,
    MatDividerModule,
    CustomFlexLayoutModule,
    MentionHighlightPipe
  ],
  templateUrl: './collaboration-notes.component.html',
  styleUrls: ['./collaboration-notes.component.scss']
})
export class CollaborationNotesComponent implements OnInit, OnDestroy {

  @Input() resourceType = 'LEAD';
  @Input() resourceId!: number;
  @Input() currentUser: any = {};

  notes: any[] = [];
  repliesMap: Record<number, any[]> = {};
  replyingTo: number | null = null;
  saving = false;
  page = 0;
  size = 20;
  totalPages = 0;

  noteForm!: FormGroup;
  replyForm!: FormGroup;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private collaborationService: CollaborationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.noteForm = this.fb.group({
      notes: ['', [Validators.required, Validators.minLength(1)]]
    });
    this.replyForm = this.fb.group({
      notes: ['', [Validators.required, Validators.minLength(1)]]
    });
    this.loadNotes();
  }

  loadNotes(): void {
    this.collaborationService
      .getRootNotes(this.resourceType, this.resourceId, this.page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.notes = res.content ?? res;
        this.totalPages = res.totalPages ?? 1;
        this.cdr.markForCheck();
      });
  }

  loadReplies(noteId: number): void {
    if (this.repliesMap[noteId]) {
      // toggle visibility
      delete this.repliesMap[noteId];
      this.cdr.markForCheck();
      return;
    }
    this.collaborationService
      .getReplies(noteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((replies: any[]) => {
        this.repliesMap[noteId] = replies;
        this.cdr.markForCheck();
      });
  }

  submitNote(): void {
    if (this.noteForm.invalid || this.saving) return;
    this.saving = true;
    const payload = {
      notes: this.noteForm.value.notes,
      resourceType: this.resourceType,
      resourceId: this.resourceId,
      createdBy: this.currentUser?.name ?? '',
      noteType: 'NOTE'
    };
    this.collaborationService.saveNote(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.noteForm.reset();
          this.saving = false;
          this.page = 0;
          this.loadNotes();
        },
        error: () => { this.saving = false; this.cdr.markForCheck(); }
      });
  }

  startReply(noteId: number): void {
    this.replyingTo = noteId;
    this.replyForm.reset();
  }

  cancelReply(): void {
    this.replyingTo = null;
  }

  submitReply(parentNoteId: number): void {
    if (this.replyForm.invalid || this.saving) return;
    this.saving = true;
    const payload = {
      notes: this.replyForm.value.notes,
      resourceType: this.resourceType,
      resourceId: this.resourceId,
      parentNoteId,
      createdBy: this.currentUser?.name ?? '',
      noteType: 'REPLY'
    };
    this.collaborationService.saveNote(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.replyForm.reset();
          this.replyingTo = null;
          this.saving = false;
          // Refresh replies
          delete this.repliesMap[parentNoteId];
          this.loadReplies(parentNoteId);
        },
        error: () => { this.saving = false; this.cdr.markForCheck(); }
      });
  }

  deleteNote(noteId: number): void {
    this.collaborationService.deleteNote(noteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.notes = this.notes.filter(n => n.noteId !== noteId);
        this.cdr.markForCheck();
      });
  }

  loadMore(): void {
    if (this.page + 1 >= this.totalPages) return;
    this.page++;
    this.collaborationService
      .getRootNotes(this.resourceType, this.resourceId, this.page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        const more = res.content ?? res;
        this.notes = [...this.notes, ...more];
        this.cdr.markForCheck();
      });
  }

  trackByNoteId(_: number, note: any): number {
    return note.noteId;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
